// The holidays business days skip besides weekends (Gilad, 2026-10-20). Pure: no network, no
// clock. The backend fetches Hebcal into memory (`backend/src/routing/holidays.ts`), and counts the
// committed snapshot (`hebcal-yomtov.json`) when it cannot; the pages and the reports get the same
// days from `/api/routing/holidays`.
//
// - US: the 6 days carriers and the warehouse do not work (confirmed by Isaac). Computed here, one
//   line each; no "observed" shift: one on a weekend is not a business day anyway.
// - Jewish: Hebcal's major holidays on the Diaspora schedule (the warehouses are in the US), only
//   the `yomtov` days people take off — Rosh Hashana ×2, Yom Kippur, Sukkot ×2, Shmini Atzeret,
//   Simchat Torah, Pesach first 2 and last 2, Shavuot ×2. Never erev days, Chol HaMoed, Chanukah,
//   Purim or the modern holidays.

import type { Holidays } from "./days";
import snapshot from "./hebcal-yomtov.json";

export type HolidaySource = "us" | "hebcal";

export interface Holiday {
  day: string;
  source: HolidaySource;
  title: string;
  hebrew?: string | null;
}

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
/** 0 = Sunday … 6 = Saturday, of a calendar day. */
const weekday = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d)).getUTCDay();
/** The `nth` (1-based) `dow` of a month. */
const nth = (y: number, m: number, dow: number, n: number) =>
  ymd(y, m, 1 + ((dow - weekday(y, m, 1) + 7) % 7) + 7 * (n - 1));
/** The last `dow` of a month. */
const last = (y: number, m: number, dow: number) => {
  const end = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return ymd(y, m, end - ((weekday(y, m, end) - dow + 7) % 7));
};

const MON = 1;
const THU = 4;

/** The US holidays: adding one is one line here (and a check). */
const US_HOLIDAYS: readonly { title: string; day: (year: number) => string }[] = [
  { title: "New Year's Day", day: (y) => ymd(y, 1, 1) },
  { title: "Memorial Day", day: (y) => last(y, 5, MON) },
  { title: "Independence Day", day: (y) => ymd(y, 7, 4) },
  { title: "Labor Day", day: (y) => nth(y, 9, MON, 1) },
  { title: "Thanksgiving", day: (y) => nth(y, 11, THU, 4) },
  { title: "Christmas Day", day: (y) => ymd(y, 12, 25) },
];

export const usHolidays = (year: number): Holiday[] =>
  US_HOLIDAYS.map((h) => ({ day: h.day(year), source: "us", title: h.title }));

/** The years the app keeps: last year to next year, so a window across the year end counts. */
export const holidayYears = (today: string): number[] => {
  const y = Number(today.slice(0, 4));
  return [y - 1, y, y + 1];
};

/** Hebcal's request for those years: major holidays, Diaspora (no `i=on`), nothing else. */
export function hebcalUrl(years: readonly number[]): string {
  const start = `${Math.min(...years)}-01-01`;
  const end = `${Math.max(...years)}-12-31`;
  return (
    "https://www.hebcal.com/hebcal?v=1&cfg=json&maj=on&min=off&mod=off&nx=off&ss=off&mf=off&c=off" +
    `&start=${start}&end=${end}`
  );
}

/** A Hebcal answer's Yom Tov days: `yomtov: true` only, one per day. Anything malformed is left out. */
export function hebcalYomTov(json: unknown): Holiday[] {
  const items = (json as { items?: unknown } | null)?.items;
  if (!Array.isArray(items)) return [];
  const out = new Map<string, Holiday>();
  for (const item of items as Record<string, unknown>[]) {
    if (item?.yomtov !== true || typeof item.date !== "string" || typeof item.title !== "string") continue;
    const day = item.date.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || out.has(day)) continue;
    out.set(day, {
      day,
      source: "hebcal",
      title: item.title,
      hebrew: typeof item.hebrew === "string" ? item.hebrew : null,
    });
  }
  return [...out.values()];
}

/**
 * The committed Hebcal snapshot: the Yom Tov days for every year it covers (2025–2032 today).
 * `scripts/update-hebcal-snapshot.ts` regenerates it; routing-checks checks it.
 */
export const HEBCAL_SNAPSHOT: { from: string; to: string; fetched: string; days: Holiday[] } = {
  from: snapshot.from,
  to: snapshot.to,
  fetched: snapshot.fetched,
  days: snapshot.days.map((d) => ({ ...d, source: "hebcal" })),
};

/** The years a list of Hebcal days has (a year it covers always has some). */
const yearsOf = (days: readonly Holiday[]) => new Set(days.map((d) => Number(d.day.slice(0, 4))));

/**
 * The Yom Tov days for `years`: the live answer where it has a year, else the snapshot's, and the
 * years neither has (`missing`: then the holidays are degraded).
 */
export function yomTovFor(
  years: readonly number[],
  live: readonly Holiday[] | null,
): { days: Holiday[]; from: "live" | "snapshot" | "both"; missing: number[] } {
  const liveYears = yearsOf(live ?? []);
  const snapYears = yearsOf(HEBCAL_SNAPSHOT.days);
  const fromLive = years.filter((y) => liveYears.has(y));
  const fromSnap = years.filter((y) => !liveYears.has(y) && snapYears.has(y));
  const pick = (list: readonly Holiday[], ys: readonly number[]) =>
    list.filter((d) => ys.includes(Number(d.day.slice(0, 4))));
  return {
    days: [...pick(live ?? [], fromLive), ...pick(HEBCAL_SNAPSHOT.days, fromSnap)].sort((a, b) =>
      a.day.localeCompare(b.day),
    ),
    from: fromSnap.length ? (fromLive.length ? "both" : "snapshot") : "live",
    missing: years.filter((y) => !liveYears.has(y) && !snapYears.has(y)),
  };
}

/** `ROUTING_HOLIDAYS`: which holidays count. */
export interface HolidaySetting {
  us: boolean;
  hebcal: boolean;
}
export const ROUTING_HOLIDAYS_DEFAULT = "us,hebcal";

/** `us,hebcal` (default) | `us` | `none`; throws on anything else, so a typo never counts nothing. */
export function holidaySetting(value: string | undefined): HolidaySetting {
  const v = (value ?? "").trim().toLowerCase() || ROUTING_HOLIDAYS_DEFAULT;
  if (v === "none") return { us: false, hebcal: false };
  const parts = v.split(",").map((p) => p.trim());
  const bad = parts.filter((p) => p !== "us" && p !== "hebcal");
  if (bad.length || !parts.length)
    throw new Error(`ROUTING_HOLIDAYS must be "us,hebcal", "us" or "none" — not "${value}"`);
  return { us: parts.includes("us"), hebcal: parts.includes("hebcal") };
}

/** The calendar business days skip: the holidays the setting keeps, day → name (two on one day: both). */
export function holidayCalendar(list: readonly Holiday[], setting: HolidaySetting): Holidays {
  const cal = new Map<string, string>();
  for (const h of list) {
    if (!setting[h.source]) continue;
    const had = cal.get(h.day);
    cal.set(h.day, had && had !== h.title ? `${had} / ${h.title}` : h.title);
  }
  return cal;
}

/** "Sukkot II" → "Sukkot", "Rosh Hashana 5787" → "Rosh Hashana": the name a run of days shares. */
const baseName = (title: string) => title.replace(/\s+(?:[IVX]+|\d{4})$/, "");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const short = (day: string) => `${MONTHS[Number(day.slice(5, 7)) - 1]} ${Number(day.slice(8, 10))}`;
const nextDay = (day: string) => new Date(Date.parse(`${day}T12:00:00Z`) + 86_400_000).toISOString().slice(0, 10);

/**
 * Skipped holidays in words: consecutive days of one holiday as one run — "Sukkot (Sep 26–27),
 * Thanksgiving (Nov 26)".
 */
export function skippedText(skipped: readonly { day: string; title: string }[]): string {
  const runs: { name: string; from: string; to: string }[] = [];
  for (const h of [...skipped].sort((a, b) => a.day.localeCompare(b.day))) {
    const name = baseName(h.title);
    const prev = runs.at(-1);
    if (prev && prev.name === name && nextDay(prev.to) === h.day) prev.to = h.day;
    else runs.push({ name, from: h.day, to: h.day });
  }
  return runs
    .map((r) => {
      if (r.from === r.to) return `${r.name} (${short(r.from)})`;
      const sameMonth = r.from.slice(5, 7) === r.to.slice(5, 7);
      return `${r.name} (${short(r.from)}–${sameMonth ? Number(r.to.slice(8, 10)) : short(r.to)})`;
    })
    .join(", ");
}
